import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconCheck, IconMail } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { CopyButton } from "./Answers";
import { casesApi } from "./api";
import { HOST_STEPS, dayOf, failureWords, hostStep, instantOf, timeWords } from "./decisions";
import { toneColor } from "./workspace";
import { PARTY_TITLES, paperState, papersCount, partiesOf } from "./partyWords";

// The host in France and their attestation d'accueil (API
// docs/visa-decisions.md): we email the host a link; they tell us when they
// went to the town hall, when it is signed and when the original is posted;
// the customer says when it arrived. Shown only when a link exists.

export const HOST_REMINDERS_MAX = 2;

// The later of the first email and the last reminder.
export function lastHostEmail(host) {
  const times = [host?.emailed_at, host?.reminded_at].filter(Boolean);
  if (!times.length) return null;
  return times.reduce((a, b) => (instantOf(b) > instantOf(a) ? b : a));
}

function Track({ status }) {
  const current = hostStep(status);
  const last = HOST_STEPS.length - 1;
  return (
    <Box component="ol" aria-label="Where the attestation is" sx={{ listStyle: "none", m: 0, p: 0, my: 1.5 }}>
      {HOST_STEPS.map((step, index) => {
        const reached = index <= current;
        const now = index === current;
        return (
          <Box
            component="li"
            key={step.status}
            aria-current={now ? "step" : undefined}
            sx={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "20px minmax(0, 1fr)",
              columnGap: 1.25,
              alignItems: "center",
              pb: index === last ? 0 : 1.25,
              // The line down to the next step: green once that step is reached.
              "&::before":
                index === last
                  ? undefined
                  : { content: '""', position: "absolute", left: 9, top: 20, bottom: 0, width: 2, bgcolor: index < current ? "success.main" : "divider" },
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: reached ? "success.main" : "background.paper",
                border: reached ? 0 : 2,
                borderColor: "divider",
                color: "common.white",
              }}
            >
              {reached ? <IconCheck size={13} stroke={3} /> : null}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: now ? 700 : 400, color: reached ? "text.primary" : "text.secondary" }}>
              <Box component="span" sx={visuallyHidden}>
                {now ? "Now: " : reached ? "Done: " : "Not yet: "}
              </Box>
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

Track.propTypes = { status: PropTypes.string };

// wide: the value on its own line under the label (a tracking number is
// too long to share the line in the case's right column).
function Fact({ label, wide, children }) {
  const row = wide ? { gridColumn: "1 / -1" } : {};
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary" sx={row}>
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, overflowWrap: "break-word", ...row }}>
        {children}
      </Typography>
    </>
  );
}

Fact.propTypes = { label: PropTypes.string, wide: PropTypes.bool, children: PropTypes.node };

export default function HostCard({ caseData, canEdit, onChanged }) {
  const dispatch = useDispatch();
  const host = caseData.host;
  if (!host) return null;
  const opened = host.opened ?? !!host.opened_at;
  const reminders = Number(host.reminders) || 0;
  const lastEmail = lastHostEmail(host);
  const received = host.status === "received";
  const stuck = host.status === "sent" && reminders >= HOST_REMINDERS_MAX;

  const resend = async () => {
    try {
      await casesApi.hostResend(caseData.id, dispatch);
      toast.success("Emailed the host again.");
      await onChanged();
    } catch (err) {
      // 4xx says why in the server's words (no email for the host, not on
      // this server's list); 503: email to hosts isn't set up here.
      toast.error(failureWords(err, err?.status === 503 ? "This server can’t email hosts yet." : "The email to the host didn’t go out. Try again."));
    }
  };

  return (
    <Card variant="outlined" id="case-host">
      <CardContent>
        <Typography variant="h6" component="h2">
          Host’s attestation
        </Typography>
        {host.host_name || host.host_email ? (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {[host.host_name, host.host_email].filter(Boolean).join(" · ")}
          </Typography>
        ) : null}
        <Track status={host.status} />
        <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.5, alignItems: "baseline" }}>
          <Fact label="Opened the link">{opened ? "Yes" : "Not yet"}</Fact>
          <Fact label="Last email">{lastEmail ? timeWords(lastEmail) : "Not emailed"}</Fact>
          <Fact label="Reminders">{reminders ? `${reminders} of ${HOST_REMINDERS_MAX}` : "None yet"}</Fact>
          {host.expires_at && !received ? <Fact label="Link works until">{dayOf(host.expires_at)}</Fact> : null}
          {host.tracking ? (
            <Fact label="Tracking" wide>
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontFamily: "monospace", fontWeight: 600, maxWidth: "100%" }}
              >
                <Box component="span" sx={{ minWidth: 0, overflowWrap: "break-word" }}>
                  {host.tracking}
                </Box>
                <CopyButton value={String(host.tracking)} label="tracking number" />
              </Box>
            </Fact>
          ) : null}
        </Box>
        {stuck ? (
          <Typography variant="body2" mt={1.25} sx={{ color: toneColor("warn"), fontWeight: 600 }}>
            Not started after {HOST_REMINDERS_MAX} reminders: call the customer about their host.
          </Typography>
        ) : null}
        {canEdit && !received ? (
          <Stack direction="row" mt={1.5}>
            <ActionButton startIcon={<IconMail size={16} />} busyLabel="Sending…" onClick={resend}>
              Email the host again
            </ActionButton>
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
}

HostCard.propTypes = { caseData: PropTypes.object.isRequired, canEdit: PropTypes.bool, onChanged: PropTypes.func.isRequired };

// A host's or sponsor's papers (API docs/reminders-and-parties.md, migration
// 031): the link we sent them, and each paper the visa asks of them with
// whether it came through the link. What they upload is attached to the
// traveller's documents, so it is reviewed in Documents like any other.
function PartyCard({ caseData, party, info, canEdit, onChanged }) {
  const dispatch = useDispatch();
  const link = info.link;
  const count = papersCount(info.papers);
  const who = party === "sponsor" ? "sponsor" : "host";
  const lastEmail = lastHostEmail(link);
  const reminders = Number(link?.reminders) || 0;

  const resend = async () => {
    try {
      await casesApi.hostResend(caseData.id, dispatch, party);
      toast.success(`Emailed the ${who} again.`);
      await onChanged();
    } catch (err) {
      toast.error(failureWords(err, err?.status === 503 ? "This server can’t email them yet." : `The email to the ${who} didn’t go out. Try again.`));
    }
  };

  return (
    <Card variant="outlined" id={`case-${party}-papers`}>
      <CardContent>
        <Typography variant="h6" component="h2">
          {PARTY_TITLES[party]}
        </Typography>
        {link?.host_name || link?.host_email ? (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {[link.host_name, link.host_email].filter(Boolean).join(" · ")}
          </Typography>
        ) : null}
        <Typography variant="body2" mt={1} fontWeight={600}>
          {count.onFile} of {count.total} on file{count.byParty ? ` · ${count.byParty} from the ${who}` : ""}
        </Typography>
        <Box component="ul" aria-label={`${PARTY_TITLES[party]}: each paper`} sx={{ listStyle: "none", m: 0, p: 0, mt: 1 }}>
          {info.papers.map((paper) => {
            const state = paperState(paper, party);
            return (
              <Box
                component="li"
                key={paper.document_id}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  columnGap: 1,
                  rowGap: 0.25,
                  py: 0.75,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {paper.name}
                </Typography>
                <Typography variant="body2" sx={{ color: toneColor(state.tone), fontWeight: 600 }}>
                  {state.label}
                  {paper.by_party && paper.uploaded_at ? ` · ${timeWords(paper.uploaded_at)}` : ""}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {count.byParty ? (
          <Typography variant="body2" color="text.secondary" mt={1}>
            What the {who} uploaded is in Documents, on each traveller it applies to, for review.
          </Typography>
        ) : null}
        <Box
          component="dl"
          sx={{ m: 0, mt: 1.25, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.5, alignItems: "baseline" }}
        >
          <Fact label="Link">{link ? (link.emailed_at ? "Sent" : "Made, not emailed") : "Not sent yet"}</Fact>
          {link ? <Fact label="Opened the link">{link.opened || link.opened_at ? "Yes" : "Not yet"}</Fact> : null}
          {link ? <Fact label="Last email">{lastEmail ? timeWords(lastEmail) : "Not emailed"}</Fact> : null}
          {link ? <Fact label="Reminders">{reminders ? `${reminders} of ${HOST_REMINDERS_MAX}` : "None yet"}</Fact> : null}
          {link?.expires_at ? <Fact label="Link works until">{dayOf(link.expires_at)}</Fact> : null}
        </Box>
        {canEdit && link?.host_email && count.requiredLeft > 0 ? (
          <Stack direction="row" mt={1.5}>
            <ActionButton startIcon={<IconMail size={16} />} busyLabel="Sending…" onClick={resend}>
              Email the {who} again
            </ActionButton>
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
}

PartyCard.propTypes = {
  caseData: PropTypes.object.isRequired,
  party: PropTypes.oneOf(["host", "sponsor"]).isRequired,
  info: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onChanged: PropTypes.func.isRequired,
};

/** The host's and sponsor's cards, for the parties the visa asks something of. */
export function PartyCards({ caseData, canEdit, onChanged }) {
  const parties = partiesOf(caseData);
  if (!parties.length) return null;
  return parties.map(([party, info]) => <PartyCard key={party} caseData={caseData} party={party} info={info} canEdit={canEdit} onChanged={onChanged} />);
}

PartyCards.propTypes = { caseData: PropTypes.object.isRequired, canEdit: PropTypes.bool, onChanged: PropTypes.func.isRequired };
